import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import {
  AppBar,
  Badge,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { UnidadeDeliveryLista, UnidadeDeliveryPublico } from '../delivery.types';
import { DeliveryService } from '../services/deliveryService';
import { useDeliveryStore } from '../store/deliveryStore';
import { fmtMoeda } from '../utils';
import StepCarrinho from './steps/StepCarrinho';
import StepCheckout from './steps/StepCheckout';
import StepConfirmacao from './steps/StepConfirmacao';
import StepMenu from './steps/StepMenu';

export default function DeliveryApp() {
  const { tenant } = useParams<{ tenant: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const {
    step, setStep,
    setTenant,
    unidades, setUnidades,
    unidade, setUnidade,
    setCatalogo,
    carrinho,
    limparCarrinho,
    restaurarDoCache,
  } = useDeliveryStore();

  const [loading, setLoading] = useState(true);
  const qtdCarrinho = carrinho.reduce((acc, i) => acc + i.quantidade, 0);

  useEffect(() => {
    if (!tenant) { navigate('/'); return; }
    sessionStorage.setItem('X-S', tenant);
    setTenant(tenant);
    carregarUnidades();
    // eslint-disable-next-line
  }, [tenant]);

  async function carregarUnidadeECatalogo(leve: UnidadeDeliveryLista) {
    setLoading(true);
    try {
      const completa = await DeliveryService.buscarUnidade(leve.chave);
      setUnidade(completa);
      const cat = await DeliveryService.buscarCatalogo(completa.codigo);
      setCatalogo(cat);
      // tenta restaurar pedido em andamento; se não houver, vai pro cardápio
      const restaurado = restaurarDoCache();
      if (!restaurado) setStep(1);
    } catch {
      toast.error('Erro ao carregar cardápio.');
    } finally {
      setLoading(false);
    }
  }

  async function carregarUnidades() {
    setLoading(true);
    try {
      const lista = await DeliveryService.listarUnidades();
      const ativas = lista.filter((u) => u.ativo && u.aceitaPedido);
      if (ativas.length === 0) {
        toast.error('Nenhuma unidade disponível no momento.');
        navigate('/');
        return;
      }
      setUnidades(ativas);
      if (ativas.length === 1) {
        await carregarUnidadeECatalogo(ativas[0]);
      } else {
        setStep(0);
        setLoading(false);
      }
    } catch {
      toast.error('Estabelecimento não encontrado.');
      navigate('/');
      setLoading(false);
    }
  }

  async function selecionarUnidade(u: UnidadeDeliveryLista) {
    await carregarUnidadeECatalogo(u);
  }

  function voltar() {
    if (step === 1 && unidades.length > 1) setStep(0);
    else if (step === 1) navigate('/');
    else if (step === 2) setStep(1);
    else if (step === 3) setStep(2);
    else navigate('/');
  }

  function tituloPasso(): string {
    if (step === 0) return 'Escolha a unidade';
    if (step === 1) return unidade?.empresaNome ?? unidade?.nome ?? '';
    if (step === 2) return 'Meu carrinho';
    if (step === 3) return 'Finalizar pedido';
    if (step === 4) return 'Pedido enviado! 🎉';
    return '';
  }

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh" gap={2}>
        <CircularProgress size={48} />
        <Typography color="text.secondary" variant="body2">Carregando cardápio...</Typography>
      </Box>
    );
  }

  // ── Menu em desktop: layout de 3 colunas sem AppBar de navegação ──────────
  if (step === 1 && isDesktop && unidade) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <AppBar position="sticky" color="primary" elevation={1}>
          <Toolbar sx={{ maxWidth: 1200, mx: 'auto', width: '100%' }}>
            <IconButton color="inherit" edge="start" onClick={voltar} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <StorefrontIcon sx={{ mr: 1, opacity: 0.85 }} />
            <Typography variant="h6" fontWeight={700} flex={1} noWrap>
              {unidade.empresaNome ?? unidade.nome}
            </Typography>
            {qtdCarrinho > 0 && (
              <Chip
                icon={<ShoppingCartIcon />}
                label={`${qtdCarrinho} ${qtdCarrinho === 1 ? 'item' : 'itens'} — ${fmtMoeda(carrinho.reduce((a, i) => a + i.valorUnitario * i.quantidade, 0))}`}
                color="secondary"
                onClick={() => setStep(2)}
                sx={{ fontWeight: 700, cursor: 'pointer' }}
              />
            )}
          </Toolbar>
        </AppBar>

        <Box sx={{ maxWidth: 1200, mx: 'auto', px: 2, py: 3, display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          <StepMenu />
        </Box>
      </Box>
    );
  }

  // ── Layout padrão (mobile + demais steps) ─────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {step !== 4 && (
        <AppBar position="sticky" color="primary" elevation={2}>
          <Toolbar>
            <IconButton color="inherit" edge="start" onClick={voltar} sx={{ mr: 0.5 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box flex={1} overflow="hidden">
              <Typography variant="h6" fontWeight={700} noWrap>
                {tituloPasso()}
              </Typography>
            </Box>
            {step === 1 && qtdCarrinho > 0 && (
              <IconButton color="inherit" onClick={() => setStep(2)}>
                <Badge badgeContent={qtdCarrinho} color="error">
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>
            )}
          </Toolbar>
        </AppBar>
      )}

      <Container maxWidth="sm" sx={{ py: 2, flex: 1 }}>
        {step === 0 && (
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Typography variant="h6" fontWeight={700} textAlign="center" mb={0.5}>
              Onde você quer pedir? 📍
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" mb={1}>
              Selecione a unidade mais próxima de você:
            </Typography>
            {unidades.map((u) => (
              <Card key={u.codigo} variant="outlined" sx={{ borderRadius: 3, transition: 'box-shadow 0.15s', '&:hover': { boxShadow: 3 } }}>
                <CardActionArea onClick={() => selecionarUnidade(u)} sx={{ p: 0.5 }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1.5} mb={0.5}>
                      <StorefrontIcon color="primary" />
                      <Typography variant="h6" fontWeight={700}>{u.nome}</Typography>
                    </Box>
                    {u.empresaEndereco && (
                      <Typography variant="body2" color="text.secondary">
                        {u.empresaEndereco}{u.empresaCidade ? ` — ${u.empresaCidade}/${u.empresaUf}` : ''}
                      </Typography>
                    )}
                    <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                      {(u.taxaEntrega ?? 0) > 0
                        ? <Chip icon={<TwoWheelerIcon />} label={`Entrega: ${fmtMoeda(u.taxaEntrega)}`} size="small" />
                        : <Chip icon={<TwoWheelerIcon />} label="Entrega grátis" size="small" color="success" />}
                      {(u.tempoEntregaMinutos ?? 0) > 0 && (
                        <Chip icon={<QueryBuilderIcon />} label={`~${u.tempoEntregaMinutos} min`} size="small" />
                      )}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}

        {step === 1 && <StepMenu />}
        {step === 2 && <StepCarrinho />}
        {step === 3 && <StepCheckout />}
        {step === 4 && (
          <StepConfirmacao onNovoPedido={() => { limparCarrinho(); setStep(1); }} />
        )}
      </Container>
    </Box>
  );
}
